import type { PermissionGroupOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, Tooltip } from "@sagot/ui";
import { matrixColumns, permissionKey, rowState, toggleKey, toggleModule } from "./matrix-model";

interface PermissionMatrixProps {
  groups: readonly PermissionGroupOut[];
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** Locked (built-in) roles and read-only sessions render the matrix without controls. */
  readOnly: boolean;
  /** Permissions the editor holds (nobody can grant a permission they don't hold). */
  grantable: ReadonlySet<string>;
}

function NotApplicable() {
  const { t } = useTranslation();
  return (
    <span className="text-fg-subtle" aria-label={t("roles.matrix.unavailable")} title={t("roles.matrix.unavailable")}>
      —
    </span>
  );
}

interface CellProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  notHeld: boolean;
  onToggle: (on: boolean) => void;
}

function Cell({ label, checked, disabled, notHeld, onToggle }: CellProps) {
  const { t } = useTranslation();
  const box = <Checkbox aria-label={label} checked={checked} disabled={disabled} onCheckedChange={(state) => { onToggle(state === true); }} />;
  if (!notHeld) return box;
  return (
    <Tooltip content={t("roles.matrix.notHeld")}>
      <span className="inline-flex">{box}</span>
    </Tooltip>
  );
}

/**
 * Grouped matrix: modules down the side × actions across (built from `GET /v1/permissions`).
 * Combinations the catalogue doesn't define render "—".
 */
export function PermissionMatrix({ groups, value, onChange, readOnly, grantable }: PermissionMatrixProps) {
  const { t } = useTranslation();
  const columns = matrixColumns(groups);
  const moduleLabel = (module: string) => t(`roles.modules.${module}`, { defaultValue: module });
  const actionLabel = (action: string) => t(`roles.actions.${action}`, { defaultValue: action });
  return (
    // Scrolls sideways on small screens; focusable so keyboard users can scroll a read-only matrix.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div className="overflow-x-auto" role="region" aria-label={t("roles.matrix.title")} tabIndex={0}>
      <table className="table">
        <thead>
          <tr>
            <th scope="col" className="sticky left-0 z-10 min-w-44 bg-surface-2">
              {t("roles.matrix.module")}
            </th>
            {columns.map((action) => (
              <th key={action} scope="col" className="whitespace-nowrap px-2 text-center">
                {actionLabel(action)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const state = rowState(value, group);
            const rowGrantable = group.permissions.some((p) => grantable.has(p.key));
            return (
              <tr key={group.module}>
                <th scope="row" className="sticky left-0 z-10 bg-surface text-left font-semibold">
                  <span className="flex items-center gap-2.5">
                    <Checkbox
                      aria-label={t("roles.matrix.selectRow", { module: moduleLabel(group.module) })}
                      checked={state}
                      disabled={readOnly || !rowGrantable}
                      onCheckedChange={(next) => { onChange(toggleModule(value, group, next === true, (key) => grantable.has(key))); }}
                    />
                    {moduleLabel(group.module)}
                  </span>
                </th>
                {columns.map((action) => {
                  const key = permissionKey(group, action);
                  const notHeld = key !== undefined && !grantable.has(key) && !readOnly;
                  return (
                    <td key={action} className="px-2 text-center">
                      {key ? (
                        <Cell
                          label={t("roles.matrix.toggle", { module: moduleLabel(group.module), action: actionLabel(action) })}
                          checked={value.has(key)}
                          disabled={readOnly || notHeld}
                          notHeld={notHeld}
                          onToggle={(on) => { onChange(toggleKey(value, key, on)); }}
                        />
                      ) : (
                        <NotApplicable />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
